import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { ArrowRight, History } from "lucide-react";
import { usePrefs } from "@/context/PrefsContext";
import { api } from "@/lib/api";

export const LEVEL_COLORS = { N5: "#2f7d5b", N4: "#2457a6", N3: "#b8322a", total: "#1f1f1f" };

export function toChartRows(list) {
  return list.map((r, i) => {
    const lv = Object.fromEntries((r.levels || []).map((l) => [l.level, l.pct]));
    return { name: `#${r.attempt || i + 1}`, total: r.pct, N5: lv.N5 ?? null, N4: lv.N4 ?? null, N3: lv.N3 ?? null, date: r.created_at, rec: r.recommended_level };
  });
}

export function TrendChart({ rows, height = 260, compact = false }) {
  const { t } = usePrefs();
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={rows} margin={{ top: 8, right: 12, left: compact ? -24 : -12, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.12} />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
        <ReferenceLine y={60} stroke="#999" strokeDasharray="4 4" label={compact ? undefined : { value: "60%", position: "right", fontSize: 10 }} />
        <Tooltip formatter={(v, k) => [`${v}%`, k === "total" ? t("totalScore") : k]} labelFormatter={(l, p) => `${l} · ${p?.[0]?.payload?.date ? new Date(p[0].payload.date).toLocaleDateString("id-ID") : ""}`} contentStyle={{ fontSize: 12, borderRadius: 12 }} />
        {!compact && <Legend wrapperStyle={{ fontSize: 12 }} formatter={(k) => (k === "total" ? t("totalScore") : k)} />}
        <Line type="monotone" dataKey="total" stroke={LEVEL_COLORS.total} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
        <Line type="monotone" dataKey="N5" stroke={LEVEL_COLORS.N5} strokeWidth={1.8} dot={{ r: 2.5 }} connectNulls />
        <Line type="monotone" dataKey="N4" stroke={LEVEL_COLORS.N4} strokeWidth={1.8} dot={{ r: 2.5 }} connectNulls />
        <Line type="monotone" dataKey="N3" stroke={LEVEL_COLORS.N3} strokeWidth={1.8} dot={{ r: 2.5 }} connectNulls />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default function AssessmentHistory() {
  const { t, area } = usePrefs();
  const nav = useNavigate();
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/assessment/history").then((r) => setList(r.data)); }, []);
  if (!list) return <p className="text-sm text-muted-foreground">{t("loading")}</p>;
  const rows = toChartRows(list);
  const first = list[0], last = list[list.length - 1];
  const delta = first && last && list.length > 1 ? last.pct - first.pct : null;
  const open = async (r) => { const { data } = await api.get(`/assessment/results/${r.id}`); nav("/assessment/result", { state: { result: data } }); };

  return (
    <div className="mx-auto max-w-3xl space-y-5 fade-up" data-testid="assessment-history-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-jpserif text-sm tracking-[0.3em] text-muted-foreground">診断の記録</p>
          <h1 className="mt-2 font-jpserif text-3xl sm:text-4xl">{t("historyTitle")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t("historyDesc")}</p>
        </div>
        <Link data-testid="history-new-assessment" to="/assessment" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">{t("retakeAssessment")}<ArrowRight className="h-4 w-4" /></Link>
      </div>
      {list.length === 0 ? (
        <div className="soft-card p-8 text-center text-sm text-muted-foreground" data-testid="history-empty"><History className="mx-auto mb-3 h-8 w-8 opacity-40" />{t("noAssessment")}</div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3" data-testid="history-stats">
            <div className="soft-card p-4"><p className="text-xs uppercase tracking-wide text-muted-foreground">{t("attempts")}</p><p className="mt-1 font-jpserif text-3xl" data-testid="history-count">{list.length}</p></div>
            <div className="soft-card p-4"><p className="text-xs uppercase tracking-wide text-muted-foreground">{t("latestScore")}</p><p className="mt-1 font-jpserif text-3xl" data-testid="history-latest">{last.pct}%</p></div>
            <div className="soft-card p-4"><p className="text-xs uppercase tracking-wide text-muted-foreground">{t("progressSinceFirst")}</p><p className={`mt-1 font-jpserif text-3xl ${delta > 0 ? "text-sage" : delta < 0 ? "text-primary" : ""}`} data-testid="history-delta">{delta == null ? "—" : `${delta > 0 ? "+" : ""}${delta}`}</p></div>
          </div>
          <div className="soft-card p-5" data-testid="history-chart">
            <p className="text-sm font-semibold">{t("trendTitle")}</p>
            <p className="mb-3 text-xs text-muted-foreground">{t("trendDesc")}</p>
            <TrendChart rows={rows} />
          </div>
          <ul className="soft-card divide-y divide-border/60 sm:hidden" data-testid="history-cards">
            {[...list].reverse().map((r) => {
              const lv = Object.fromEntries((r.levels || []).map((l) => [l.level, l.pct]));
              return (
                <li key={r.id}>
                  <button data-testid={`history-card-${r.attempt}`} onClick={() => open(r)} className="flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors active:bg-accent">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-muted text-sm font-semibold">{r.attempt}</span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2"><b className="text-base">{r.pct}%</b><span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">{r.recommended_level}</span></span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })} · {r.score}/{r.total}</span>
                      <span className="mt-1.5 flex flex-wrap gap-x-3 text-xs">{["N5", "N4", "N3"].map((k) => <span key={k} style={{ color: LEVEL_COLORS[k] }}>{k} {lv[k] ?? "—"}%</span>)}</span>
                      {(r.weak_areas || []).length > 0 && <span className="mt-1 block text-xs text-muted-foreground">{t("weakAreas")}: {r.weak_areas.map(area).join(", ")}</span>}
                    </span>
                    <ArrowRight className="mt-2 h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="soft-card hidden overflow-x-auto sm:block" data-testid="history-table">
            <table className="w-full min-w-[640px] text-sm">
              <thead><tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground"><th className="px-4 py-2">#</th><th className="px-4 py-2">{t("date")}</th><th className="px-4 py-2">{t("totalScore")}</th><th className="px-4 py-2">N5 · N4 · N3</th><th className="px-4 py-2">{t("recommendedLevel")}</th><th className="px-4 py-2">{t("weakAreas")}</th></tr></thead>
              <tbody>
                {[...list].reverse().map((r) => {
                  const lv = Object.fromEntries((r.levels || []).map((l) => [l.level, l.pct]));
                  return (
                    <tr key={r.id} data-testid={`history-row-${r.attempt}`} onClick={() => open(r)} className="cursor-pointer border-b border-border/60 transition-colors last:border-0 hover:bg-accent">
                      <td className="px-4 py-3 font-semibold">{r.attempt}</td>
                      <td className="px-4 py-3 text-muted-foreground">{new Date(r.created_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}</td>
                      <td className="px-4 py-3"><b>{r.pct}%</b> <span className="text-xs text-muted-foreground">({r.score}/{r.total})</span></td>
                      <td className="px-4 py-3 text-xs">{["N5", "N4", "N3"].map((k) => <span key={k} className="mr-2" style={{ color: LEVEL_COLORS[k] }}>{lv[k] ?? "—"}%</span>)}</td>
                      <td className="px-4 py-3"><span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">{r.recommended_level}</span></td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{(r.weak_areas || []).map(area).join(", ") || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
